/**
 * Booking Day Hints
 *
 * The month calendar in the public booking flow shows only WHETHER a day has
 * availability (a blue pill plus a dot). A prospect scanning for "a day with
 * room to talk" had to click each day one at a time to find out that one had a
 * single 8:00 AM slot left and the next had nine. These helpers turn a day's
 * slots into the short summary the calendar shows on hover/focus, so the shape
 * of the week is readable without clicking.
 *
 * Everything here is pure and takes the display zone explicitly, so the
 * summary matches the times the booker actually sees (see `bookingTimezones`).
 * Nothing throws on malformed input — a bad ISO string degrades to the count
 * without a time window rather than taking the calendar down.
 */

import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { AvailableDay, DEFAULT_TIMEZONE, TimeSlot } from './bookingConstants.js';
import { formatTimeZoneAbbreviation, isValidTimeZone } from './bookingTimezones.js';

export interface DayAvailabilitySummary {
  /** Number of bookable slots on the day. */
  slotCount: number;
  /** How many of those slots fall over lunch (`isLunchSlot`). */
  lunchSlotCount: number;
  /** e.g. `5 times available`, `1 time available`, `No times available`. */
  countLabel: string;
  /**
   * Earliest start → latest end in the display zone, e.g.
   * `9:00 AM – 4:30 PM CDT`. `null` when there are no slots, or when every
   * slot timestamp is unparseable.
   */
  windowLabel: string | null;
  /**
   * One line combining the two, which is what the calendar renders:
   * `5 times available · 9:00 AM – 4:30 PM CDT`.
   */
  hint: string;
}

/** Slots whose start AND end parse as real instants, sorted by start. */
function parsableSlots(slots: readonly TimeSlot[]): Array<{ start: Date; end: Date }> {
  const parsed: Array<{ start: Date; end: Date }> = [];
  for (const slot of slots) {
    const start = safeParse(slot.start);
    const end = safeParse(slot.end);
    if (!start || !end) continue;
    parsed.push({ start, end });
  }
  return parsed.sort((a, b) => a.start.getTime() - b.start.getTime());
}

function safeParse(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  try {
    const parsed = parseISO(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * `5 times available` / `1 time available` / `No times available`.
 *
 * Exported on its own because the times sidebar renders the same phrase and
 * the two must never disagree.
 */
export function formatSlotCountLabel(slotCount: number): string {
  if (slotCount <= 0) return 'No times available';
  return `${slotCount} time${slotCount === 1 ? '' : 's'} available`;
}

/**
 * One slot as `9:00 AM – 9:30 AM CDT` in the display zone.
 *
 * The slot pills render only the START time (that is all that fits), so this is
 * what the hover/focus tip adds: the end time, which is the only way to see how
 * long the meeting actually is, and the zone abbreviation. Returns `null` when
 * either timestamp is unparseable so the caller can drop the tip instead of
 * showing "Invalid time".
 */
export function formatSlotRange(slot: TimeSlot, timeZone: string): string | null {
  const start = safeParse(slot.start);
  const end = safeParse(slot.end);
  if (!start || !end) return null;

  const safeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIMEZONE;
  const startText = formatInTimeZone(start, safeZone, 'h:mm a');
  const endText = formatInTimeZone(end, safeZone, 'h:mm a');
  const abbreviation = formatTimeZoneAbbreviation(safeZone, start);
  return `${startText} – ${endText} ${abbreviation}`;
}

/**
 * Summarize one day's availability for display in `timeZone`.
 *
 * @param day       The day, or `undefined`/`null` for a day with no data at all
 *                  (an unavailable calendar cell) — yields the empty summary.
 * @param timeZone  IANA display zone. Falls back to the business default when
 *                  invalid, matching every other formatter in this flow.
 */
export function summarizeDayAvailability(
  day: AvailableDay | null | undefined,
  timeZone: string
): DayAvailabilitySummary {
  const slots = day?.slots ?? [];
  const slotCount = slots.length;
  const lunchSlotCount = slots.filter((slot) => slot.isLunchSlot).length;
  const countLabel = formatSlotCountLabel(slotCount);

  if (slotCount === 0) {
    return { slotCount: 0, lunchSlotCount: 0, countLabel, windowLabel: null, hint: countLabel };
  }

  const safeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIMEZONE;
  const parsed = parsableSlots(slots);

  if (parsed.length === 0) {
    // Count is still trustworthy (it came from the array length); only the
    // window is unknowable. Show the half we have rather than nothing.
    return { slotCount, lunchSlotCount, countLabel, windowLabel: null, hint: countLabel };
  }

  const first = parsed[0].start;
  // Latest END, not latest start: the window should read as the span a booker
  // can occupy, so a 4:00 PM slot on a 30-minute event type ends at 4:30 PM.
  const last = parsed.reduce((latest, slot) => (slot.end > latest ? slot.end : latest), parsed[0].end);

  const startText = formatInTimeZone(first, safeZone, 'h:mm a');
  const endText = formatInTimeZone(last, safeZone, 'h:mm a');
  const abbreviation = formatTimeZoneAbbreviation(safeZone, first);

  const windowLabel =
    startText === endText
      ? `${startText} ${abbreviation}`
      : `${startText} – ${endText} ${abbreviation}`;

  return {
    slotCount,
    lunchSlotCount,
    countLabel,
    windowLabel,
    hint: `${countLabel} · ${windowLabel}`,
  };
}
